import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, TrendingUp, Clock, PoundSterling, Users, Shield, CheckCircle, Building2, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CaseStudy {
  sector: 'public' | 'private';
  clientName: string;
  clientType: string;
  contractValue?: string;
  image: string;
  challenge: string;
  solution: string;
  results: {
    icon: React.ElementType;
    value: string;
    label: string;
    color: string;
  }[];
  quote: string;
  quoteAuthor: string;
  timeline: string;
  framework?: string;
}

const caseStudies: CaseStudy[] = [
  {
    sector: 'public',
    clientName: 'Metropolitan Borough Council',
    clientType: 'Local Government',
    contractValue: '£85,000',
    framework: 'G-Cloud 14 Direct Award',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80',
    challenge: '30,000 planning application backlog with 45-day average processing time causing resident complaints',
    solution: 'Deployed 6 AI document processing robots with automated workflow routing and compliance checking',
    results: [
      { icon: TrendingUp, value: '30,000', label: 'Applications processed', color: 'text-green-400' },
      { icon: Clock, value: '18:1', label: 'ROI ratio achieved', color: 'text-blue-400' },
      { icon: PoundSterling, value: '£425K', label: 'Annual savings', color: 'text-purple-400' }
    ],
    quote: 'The system paid for itself in under 10 weeks. Residents now get decisions in days, not months.',
    quoteAuthor: 'Director of Planning Services',
    timeline: '6-week deployment'
  },
  {
    sector: 'private',
    clientName: 'National Retail Chain',
    clientType: 'E-commerce & Retail',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=2000&q=80',
    challenge: 'Manual order processing causing delays, inventory errors, and customer service bottlenecks across 50 locations',
    solution: 'End-to-end automation from order to delivery with intelligent customer service routing and real-time inventory sync',
    results: [
      { icon: TrendingUp, value: '300%', label: 'Order volume handled', color: 'text-green-400' },
      { icon: Clock, value: '95%', label: 'Faster processing', color: 'text-blue-400' },
      { icon: PoundSterling, value: '£680K', label: 'Annual savings', color: 'text-purple-400' }
    ],
    quote: 'We scaled our operations without hiring additional staff. The ROI exceeded our projections by 40%.',
    quoteAuthor: 'Operations Director',
    timeline: '8-week implementation'
  },
  {
    sector: 'public',
    clientName: 'NHS Foundation Trust',
    clientType: 'Healthcare',
    contractValue: '£124,000',
    framework: 'G-Cloud 14 via NHS SBS',
    image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=2000&q=80',
    challenge: '180,000 patient record backlog with handwritten notes causing clinical delays and compliance risks',
    solution: 'AI OCR document processing with medical terminology training and secure UK-only data storage',
    results: [
      { icon: Users, value: '180K', label: 'Records digitized', color: 'text-green-400' },
      { icon: Clock, value: '250K hrs', label: 'Clinical time saved', color: 'text-blue-400' },
      { icon: Shield, value: '100%', label: 'UK data compliance', color: 'text-purple-400' }
    ],
    quote: 'This freed up our clinical staff to focus on patient care. The ROI exceeded expectations by 40%.',
    quoteAuthor: 'Chief Information Officer',
    timeline: '8-week phased deployment'
  },
  {
    sector: 'private',
    clientName: 'Financial Services Firm',
    clientType: 'Banking & Finance',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=2000&q=80',
    challenge: 'KYC document verification taking 5-7 days per customer, creating onboarding bottlenecks and compliance risks',
    solution: 'AI-powered document verification with fraud detection, automated compliance checking, and risk scoring',
    results: [
      { icon: Clock, value: '24 hrs', label: 'New KYC timeframe', color: 'text-green-400' },
      { icon: CheckCircle, value: '99.8%', label: 'Accuracy rate', color: 'text-blue-400' },
      { icon: PoundSterling, value: '£420K', label: 'Annual savings', color: 'text-purple-400' }
    ],
    quote: 'We onboard customers in hours instead of days while maintaining compliance. Game-changing for our growth.',
    quoteAuthor: 'Head of Compliance',
    timeline: '6-week implementation'
  },
  {
    sector: 'public',
    clientName: 'Department for Work and Pensions',
    clientType: 'Central Government',
    contractValue: '£2,400,000',
    framework: 'G-Cloud 14 Further Competition',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=2000&q=80',
    challenge: 'Universal Credit claims processing taking 8 weeks with 50,000 claim backlog and rising costs',
    solution: 'Large-scale RPA with 24 robots, intelligent document processing, ATRS-compliant decision logging',
    results: [
      { icon: TrendingUp, value: '50,000', label: 'Claims in 2 weeks', color: 'text-green-400' },
      { icon: Clock, value: '16:1', label: 'ROI ratio (DWP)', color: 'text-blue-400' },
      { icon: PoundSterling, value: '£8.5M', label: 'Annual savings', color: 'text-purple-400' }
    ],
    quote: 'The transparency and auditability met all our ATRS requirements. This is the future of government services.',
    quoteAuthor: 'Senior Programme Director',
    timeline: '16-week phased implementation'
  },
  {
    sector: 'private',
    clientName: 'Healthcare Provider Group',
    clientType: 'Private Healthcare',
    image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=2000&q=80',
    challenge: '30% appointment no-show rate, overwhelmed staff, and poor patient communication across 12 clinics',
    solution: 'AI voice assistant for appointments, automated reminders, intelligent scheduling with SMS integration',
    results: [
      { icon: Users, value: '85%', label: 'Attendance rate', color: 'text-green-400' },
      { icon: TrendingUp, value: '200+', label: 'Calls handled daily', color: 'text-blue-400' },
      { icon: PoundSterling, value: '£300K', label: 'Revenue recovered', color: 'text-purple-400' }
    ],
    quote: 'We recovered £25K monthly in lost revenue and our staff can focus on patient care instead of phones.',
    quoteAuthor: 'Practice Manager',
    timeline: '4-week implementation'
  }
];

export default function UnifiedCaseStudies() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [filterSector, setFilterSector] = useState<'all' | 'public' | 'private'>('all');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const filteredStudies = filterSector === 'all'
    ? caseStudies
    : caseStudies.filter(study => study.sector === filterSector);

  const currentStudy = filteredStudies[activeIndex];

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActiveIndex((prev) => (prev + 1) % filteredStudies.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setActiveIndex((prev) => (prev - 1 + filteredStudies.length) % filteredStudies.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const handleFilterChange = (sector: 'all' | 'public' | 'private') => {
    setFilterSector(sector);
    setActiveIndex(0);
  };

  return (
    <section className="relative py-32 bg-gray-900 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900" />
      <div className="absolute inset-0 bg-grid opacity-[0.02]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold mb-6">
            <span className="text-gradient">Proven Results Across Sectors</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Real-world success stories from government departments, NHS trusts, councils, and leading enterprises.
            All delivering measurable ROI and operational excellence.
          </p>

          <div className="inline-flex items-center bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-1">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                filterSector === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All Sectors
            </button>
            <button
              onClick={() => handleFilterChange('public')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center space-x-2 ${
                filterSector === 'public'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Building2 className="w-4 h-4" />
              <span>Public Sector</span>
            </button>
            <button
              onClick={() => handleFilterChange('private')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center space-x-2 ${
                filterSector === 'private'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              <span>Private Sector</span>
            </button>
          </div>
        </div>

        <div className="relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative group perspective-container">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-teal-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
              <div className="relative h-[400px] rounded-2xl overflow-hidden card-3d">
                <img
                  src={currentStudy.image}
                  alt={currentStudy.clientName}
                  className="w-full h-full object-cover transform scale-105 group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
                <div className="card-shadow" />
              </div>
            </div>

            <div className="relative space-y-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-3 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    currentStudy.sector === 'public'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                  }`}>
                    {currentStudy.sector === 'public' ? (
                      <span className="flex items-center space-x-1">
                        <Building2 className="w-3 h-3" />
                        <span>{currentStudy.clientType}</span>
                      </span>
                    ) : (
                      <span className="flex items-center space-x-1">
                        <Briefcase className="w-3 h-3" />
                        <span>{currentStudy.clientType}</span>
                      </span>
                    )}
                  </span>
                  {currentStudy.contractValue && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                      {currentStudy.contractValue}
                    </span>
                  )}
                  {currentStudy.framework && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                      {currentStudy.framework}
                    </span>
                  )}
                </div>
                <h3 className="text-4xl font-bold text-white">
                  {currentStudy.clientName}
                </h3>
              </div>

              <div className="glass-card p-6 rounded-xl backdrop-blur-sm">
                <h4 className="text-sm font-semibold text-gray-400 mb-2">Challenge</h4>
                <p className="text-gray-200">{currentStudy.challenge}</p>
              </div>

              <div className="glass-card p-6 rounded-xl backdrop-blur-sm">
                <h4 className="text-sm font-semibold text-gray-400 mb-2">Solution</h4>
                <p className="text-gray-200">{currentStudy.solution}</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {currentStudy.results.map((result, index) => (
                  <div
                    key={index}
                    className="glass-card p-4 rounded-xl backdrop-blur-sm transform hover:scale-105 transition-transform duration-300 text-center"
                  >
                    <div className="flex justify-center mb-2">
                      <result.icon className={`h-6 w-6 ${result.color}`} />
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">
                      {result.value}
                    </div>
                    <div className="text-xs text-gray-400">
                      {result.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="glass-card p-6 rounded-xl backdrop-blur-sm bg-blue-500/5 border-blue-500/20">
                <p className="text-gray-200 italic mb-3">
                  "{currentStudy.quote}"
                </p>
                <p className="text-sm text-gray-400">
                  — {currentStudy.quoteAuthor}, {currentStudy.clientName}
                </p>
              </div>

              <div className="glass-card p-3 rounded-lg backdrop-blur-sm">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2 text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>Implementation:</span>
                  </div>
                  <p className="text-white font-semibold">{currentStudy.timeline}</p>
                </div>
              </div>
            </div>
          </div>

          {filteredStudies.length > 1 && (
            <>
              <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between pointer-events-none">
                <button
                  onClick={handlePrev}
                  className="transform -translate-x-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all pointer-events-auto"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={handleNext}
                  className="transform translate-x-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all pointer-events-auto"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex justify-center mt-8 gap-2">
                {filteredStudies.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-12 h-1 rounded-full transition-all duration-300 ${
                      index === activeIndex
                        ? 'bg-teal-500'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-20">
          <div className="max-w-3xl mx-auto mb-10">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Achieve Similar Results?</h3>
            <p className="text-lg text-gray-300">
              Whether you're in the public or private sector, these outcomes are achievable for your organization.
              Let's discuss how we can deliver measurable ROI with full compliance and security.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-md text-white bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>Book Free Consultation</span>
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/tools/roi-calculator"
              className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-md text-gray-300 border-2 border-gray-600 hover:border-gray-500 hover:text-white transition-all duration-200"
            >
              <span>Calculate Your ROI</span>
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
